'use client';

import { useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import type {
  Reference,
  ReferenceCategory,
} from '@eliadmin/core/src/api/references/references-types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ReferenceCardSkeleton } from './reference-card-skeleton';
import { ReferenceCard } from './reference-card';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const ALL_CATEGORIES_TAB = 'all';

export function ReferencesList({
  categories,
  locale,
}: {
  categories: ReferenceCategory[];
  locale: string;
}) {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, activeTab],
    queryFn: ({ pageParam = 1 }) => {
      return fetchReferences({
        locale,
        query: {
          page: pageParam,
          results: 6,
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
          orderBy: 'created_at',
          order: 'desc',
        },
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage.meta.current_page < lastPage.meta.last_page) {
        return lastPage.meta.current_page + 1;
      }

      return undefined;
    },
    initialPageParam: 1,
  });

  const references = referencesQuery.data?.pages.flatMap(page => page.data) ?? [];
  const isLoading = referencesQuery.isLoading;

  return (
    <Tabs
      defaultValue={activeTab}
      onValueChange={value => setActiveTab(value)}
      value={activeTab}
      className={cn(
        'custom-section',
        'gap-8 pb-8 pr-0 sm:gap-10 sm:pb-10 md:pb-14 md:pr-4 lg:gap-12 lg:pb-16 xl:pb-[72px]',
      )}
    >
      <TabsList
        className={cn(
          'custom-container',
          'flex h-fit w-full flex-nowrap items-center justify-start gap-4 overflow-x-scroll rounded-none bg-transparent p-0 md:justify-center md:overflow-x-visible',
          'hide-scroll-bar',
          'ea-references-list__tabs-list',
        )}
      >
        <TabsTrigger className={cn('ea-references-list__tabs-trigger')} value={ALL_CATEGORIES_TAB}>
          {t('text1947')}
        </TabsTrigger>
        {categories.map(category => (
          <TabsTrigger
            key={category.id}
            className={cn('ea-references-list__tabs-trigger')}
            value={String(category.id)}
          >
            {category.name}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent
        value={activeTab}
        className={cn(
          'custom-container',
          'flex w-full flex-col items-center justify-center gap-8 sm:gap-10 lg:gap-12',
          'pr-4 md:pr-0',
        )}
      >
        {isLoading && (
          <>
            {[...Array(6)].map((_, index) => (
              <ReferenceCardSkeleton index={index} key={index} />
            ))}
          </>
        )}
        {!isLoading && references.length === 0 && (
          <p className="min-h-[200px] w-full text-center text-sm text-muted-foreground sm:text-base lg:text-lg">
            {t('text3607')}
          </p>
        )}
        {!isLoading &&
          references.length > 0 &&
          references.map((reference, index) => (
            <ReferenceCard index={index} reference={reference} key={reference.id} headingTag="h2" />
          ))}
        <div
          className={cn('flex w-full items-center justify-center', {
            hidden: !referencesQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => referencesQuery.fetchNextPage()}
            isLoading={referencesQuery.isFetchingNextPage}
            variant={'dark'}
          >
            {t('text3606')}
          </Button>
        </div>
      </TabsContent>
    </Tabs>
  );
}
